import { getBrowserLanguageTags, matchBrowserLocale } from '@/components/shared/utils/resolveBrowserLocale'

function languagePart(localeCode) {
  return String(localeCode || '').split('-')[0].toLowerCase()
}

function normalizeLocaleKey(localeCode) {
  return String(localeCode || '').trim().toLowerCase().replace(/_/g, '-')
}

/**
 * 去掉 i18n JSON 的 `_usage` 文档字段，避免被当成一种语言。
 * @param {Record<string, unknown>} [table]
 */
export function stripI18nUsage(table) {
  if (!table || typeof table !== 'object') return {}
  const { _usage, ...langs } = table
  return langs
}

/**
 * 从按语言键组织的文案表中解析当前 locale 对应文案。
 * 匹配顺序：完整 locale 代码 → 语言简码 → 英文 → 首项。
 */
export function pickUiMessages(localeCode, messagesByLang) {
  const map = messagesByLang || {}
  const normalized = normalizeLocaleKey(localeCode)
  const lang = languagePart(normalized)

  if (map[normalized]) return map[normalized]
  if (map[lang]) return map[lang]
  if (map.en) return map.en

  const keys = Object.keys(map)
  return keys.length ? map[keys[0]] : {}
}

function buildLocaleOptionsFromMessageKeys(messagesByLang) {
  return Object.keys(messagesByLang || {}).map((code) => ({ code, enabled: true }))
}

/**
 * 按浏览器首选语言，在文案表可用语言键中匹配 locale。
 */
export function resolveBrowserUiLocale(messagesByLang, browserTags) {
  const tags = browserTags?.length ? browserTags : getBrowserLanguageTags()
  const enabled = buildLocaleOptionsFromMessageKeys(messagesByLang)
  return matchBrowserLocale(tags, enabled) || 'en'
}

/**
 * 直接按浏览器语言解析 UI 文案。
 */
export function pickUiMessagesFromBrowser(messagesByLang, browserTags) {
  const locale = resolveBrowserUiLocale(messagesByLang, browserTags)
  return pickUiMessages(locale, messagesByLang)
}

/**
 * 优先使用站点当前语言；未就绪时回退浏览器语言。
 */
export function pickUiMessagesForSite(messagesByLang, localeCode, browserTags) {
  if (localeCode) {
    return pickUiMessages(localeCode, messagesByLang)
  }
  return pickUiMessagesFromBrowser(messagesByLang, browserTags)
}

/**
 * 替换文案模板中的 `{key}` 占位符。
 * @param {string} template
 * @param {Record<string, string|number>} [vars]
 */
export function formatUiMessage(template, vars = {}) {
  const text = String(template ?? '')
  if (!text || !vars || typeof vars !== 'object') return text
  return text.replace(/\{(\w+)\}/g, (match, key) => (
    Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : match
  ))
}
